import React from 'react';
import PropTypes from 'prop-types';
import { Sentry } from '../../utils/sentry';
import { logger } from '../../utils/logger';

/**
 * @fileoverview React error boundary that catches render errors, logs to logger, reports to Sentry, and shows a fallback UI with reload.
 */

/**
 * Error boundary component. Catches errors in child tree, reports to Sentry, and renders a fallback with reload button.
 */
class ErrorBoundary extends React.Component {
	constructor(props) {
		super(props);
		this.state = { hasError: false, error: null, errorInfo: null };
	}

	/**
	 * Updates state when a descendant throws so we can render fallback UI.
	 * @param {Error} error - The thrown error.
	 * @returns {{ hasError: true, error: Error }}
	 */
	static getDerivedStateFromError(error) {
		return { hasError: true, error };
	}

	/**
	 * Logs error, reports to Sentry with component stack, and stores errorInfo in state.
	 * @param {Error} error - The thrown error.
	 * @param {object} errorInfo - React error info (e.g. componentStack).
	 */
	componentDidCatch(error, errorInfo) {
		logger.error('React Error Boundary caught error:', error, errorInfo);
		Sentry.captureException(error, {
			contexts: {
				react: {
					componentStack: errorInfo.componentStack
				}
			}
		});
		this.setState({ errorInfo });
	}

	handleReload = () => {
		window.location.reload();
	};

	render() {
		if (this.state.hasError) {
			return (
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						minHeight: '100vh',
						padding: '20px',
						textAlign: 'center',
						backgroundColor: '#f5f5f5'
					}}
				>
					<div
						style={{
							maxWidth: '500px',
							backgroundColor: 'white',
							padding: '40px',
							borderRadius: '8px',
							boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
						}}
					>
						<h2 style={{ color: '#d32f2f', marginBottom: '16px' }}>
							Something went wrong
						</h2>
						<p style={{ color: '#666', marginBottom: '24px' }}>
							We&apos;ve been notified and will fix this soon.
						</p>
						{process.env.NODE_ENV === 'development' && this.state.error && (
							<details
								style={{
									marginBottom: '24px',
									padding: '16px',
									backgroundColor: '#f5f5f5',
									borderRadius: '4px',
									textAlign: 'left'
								}}
							>
								<summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
									Error Details
								</summary>
								<p
									style={{
										color: '#d32f2f',
										fontFamily: 'monospace',
										fontSize: '12px'
									}}
								>
									{this.state.error.toString()}
								</p>
								{this.state.errorInfo && (
									<pre
										style={{
											fontSize: '10px',
											overflow: 'auto',
											maxHeight: '200px',
											color: '#666'
										}}
									>
										{this.state.errorInfo.componentStack}
									</pre>
								)}
							</details>
						)}
						<button
							type="button"
							onClick={this.handleReload}
							style={{
								padding: '12px 24px',
								backgroundColor: '#1976d2',
								color: 'white',
								border: 'none',
								borderRadius: '4px',
								fontSize: '16px',
								cursor: 'pointer',
								transition: 'background-color 0.2s'
							}}
							onMouseOver={e => {
								e.target.style.backgroundColor = '#1565c0';
							}}
							onMouseOut={e => {
								e.target.style.backgroundColor = '#1976d2';
							}}
						>
							Reload Page
						</button>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}

ErrorBoundary.propTypes = {
	children: PropTypes.node.isRequired
};

export default ErrorBoundary;
